import { useMemo } from 'react';
import { POSITIONS, positionsByWeight, WEIGHT_MAX } from '../data/positions.ts';
import { useSquad } from '../lib/squadContext.ts';
import { decimal, delta, FOREIGN_LIMIT, money, summarise } from '../lib/rules.ts';
import { C, HEADER } from '../lib/theme.ts';
import type { Candidate, HomegrownStatus, RuleInput } from '../types.ts';

/** One row of the candidate lists, with its colours already resolved. */
interface CandidateRow {
  key: string;
  candidate: Candidate;
  value: string;
  bg: string;
  fg: string;
  metaFg: string;
}

/** One cell of the live impact row. */
interface ImpactCell {
  label: string;
  after: string;
  change: string;
  fg: string;
}

const LABEL_STYLE = {
  fontSize: 9,
  letterSpacing: '0.12em',
  color: C.muted,
  fontWeight: 700,
  marginBottom: 4,
} as const;

const INPUT_STYLE = {
  width: '100%',
  border: '2px solid ' + C.ink,
  background: C.bg,
  padding: '7px 9px',
  fontSize: 12,
  fontWeight: 700,
} as const;

/** The transfer simulator: pick a profile, edit it, and read the rule impact live. */
export default function TransferModal() {
  const { state, dispatch, owned, firstTeam, listRules, squadRules } = useSquad();
  const form = state.form;

  const lists = useMemo(() => {
    const build = (candidate: Candidate, key: string): CandidateRow => {
      const active = state.activeCandidateKey === key;
      return {
        key,
        candidate,
        value: money(candidate.marketValue),
        bg: active ? C.ink : C.surface,
        fg: active ? C.bg : C.ink,
        metaFg: active ? HEADER.muted : C.muted,
      };
    };
    return {
      targets: state.targets.map((c, i) => build(c, 't' + i)),
      drafts: state.drafts.map((c, i) => build(c, 'd' + i)),
    };
  }, [state.targets, state.drafts, state.activeCandidateKey]);

  const impact = useMemo(() => {
    // The hypothetical signing, always registered on List A.
    const virtual: RuleInput = {
      birthYear: form.birthYear,
      turkeyEligible: form.turkeyEligible,
      onListA: true,
      homegrown: form.homegrown,
      wage: form.wage,
      marketValue: form.marketValue,
    };
    const listAfter = summarise<RuleInput>([...firstTeam, virtual]);
    const squadAfter = summarise<RuleInput>([...owned, virtual]);

    const foreignGrows = listAfter.foreign.length > listRules.foreign.length;
    // How many "+4" slots are claimed without a 2003-or-later foreigner to fill them.
    const gapBefore = Math.max(0, listRules.overBase - listRules.youngForeign.length);
    const gapAfter = Math.max(0, listAfter.overBase - listAfter.youngForeign.length);
    const cap = state.wageCap;

    let warning: string;
    if (foreignGrows && listAfter.foreign.length > FOREIGN_LIMIT) {
      warning =
        'Bu transfer yabancı sayısını ' +
        listRules.foreign.length +
        ' → ' +
        listAfter.foreign.length +
        " yapar ve 14'lük TFF sınırını " +
        (listAfter.foreign.length - FOREIGN_LIMIT) +
        ' oyuncu aşar; önce ' +
        (listAfter.foreign.length - FOREIGN_LIMIT) +
        " yabancıyı listeden çıkarmadan bu oyuncu A Takım Listesi'ne yazılamaz.";
    } else if (foreignGrows && gapAfter > gapBefore) {
      warning =
        'Bu transfer +4 kontenjanı açığını ' +
        gapBefore +
        ' → ' +
        gapAfter +
        ' yapar. 01.01.2003 ve sonrası doğumlu bir yabancı seçersen açık büyümez.';
    } else if (!foreignGrows && (listRules.foreign.length > FOREIGN_LIMIT || gapBefore > 0)) {
      warning =
        'Bu transfer yabancı kontenjanını etkilemiyor (yerli/A Millî uygun oyuncu). Kadrodaki mevcut ihlal duruyor: ' +
        (listRules.foreign.length > FOREIGN_LIMIT
          ? listRules.foreign.length + ' yabancı, sınır 14. '
          : '') +
        (gapBefore > 0 ? gapBefore + ' adet 2003+ doğumlu yabancı eksik.' : '');
    } else if (foreignGrows) {
      warning =
        'Yabancı sayısı ' +
        listAfter.foreign.length +
        '/14 olur — sınır içinde kalır' +
        (squadAfter.totalWage > cap
          ? '; ancak maaş yükü bütçeyi ' + money(squadAfter.totalWage - cap) + ' aşar.'
          : '.');
    } else if (squadAfter.totalWage > cap) {
      warning =
        'Kurallar açısından sorun yok; ancak maaş yükü bütçeyi ' +
        money(squadAfter.totalWage - cap) +
        ' aşar.';
    } else {
      warning =
        "Bu transfer hem TFF hem UEFA kriterlerine uygun. Liste A'da " +
        Math.max(0, listAfter.listACapacity - listAfter.listA.length) +
        ' slot boşta kalır.';
    }

    const named = form.name.trim();
    const anyPosition = positionsByWeight(form.positionWeights).length > 0;
    const cells: ImpactCell[] = [
      {
        label: 'KADRO DEĞERİ',
        after: money(squadAfter.totalValue),
        change: delta(squadAfter.totalValue - squadRules.totalValue) + 'M',
        fg: C.deep,
      },
      {
        label: 'YAŞ ORT.',
        after: decimal(squadAfter.averageAge, 1),
        change: delta(squadAfter.averageAge - squadRules.averageAge),
        fg: squadAfter.averageAge < squadRules.averageAge ? C.ink : C.deep,
      },
      {
        label: 'MAAŞ',
        after: money(squadAfter.totalWage),
        change: delta(squadAfter.totalWage - squadRules.totalWage) + 'M',
        fg: C.deep,
      },
      {
        label: 'YABANCI',
        after: listAfter.foreign.length + '/' + FOREIGN_LIMIT,
        change: listAfter.foreign.length > listRules.foreign.length ? '+1' : '0',
        fg: listAfter.tffViolation ? C.red : C.ink,
      },
    ];

    return {
      badge: named ? 'CANLI ÖNİZLEME' : 'OYUNCU ADI GİR',
      addBg: named && anyPosition ? C.red : C.line,
      addFg: named && anyPosition ? C.bg : C.muted,
      addLabel: named
        ? anyPosition
          ? 'KADROYA EKLE'
          : 'EN AZ BİR MEVKİ SEÇ'
        : 'ÖNCE OYUNCU ADI GİR',
      cells,
      warning,
    };
  }, [form, firstTeam, owned, listRules, squadRules, state.wageCap]);

  if (!state.transferModalOpen) return null;

  const renderCandidate = (row: CandidateRow, withName: boolean) => (
    <div
      key={row.key}
      onClick={() =>
        dispatch({ type: 'applyCandidate', candidate: row.candidate, key: row.key, withName })
      }
      style={{
        padding: '11px 14px',
        borderBottom: '1px solid ' + C.line,
        cursor: 'pointer',
        background: row.bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '-0.01em',
            color: row.fg,
          }}
        >
          {row.candidate.name}
        </span>
        <span style={{ fontSize: 10, fontWeight: 800, color: row.metaFg }}>{row.value}</span>
      </div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: row.metaFg, marginTop: 3 }}>
        {row.candidate.summary}
      </div>
    </div>
  );

  return (
    <div
      onClick={() => dispatch({ type: 'setTransferModal', open: false })}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(32,30,29,0.58)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '48px 24px',
        overflow: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 1060,
          maxWidth: '100%',
          background: C.bg,
          border: '2px solid ' + C.ink,
          boxShadow: '0 24px 48px rgba(32,30,29,0.28)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            background: C.ink,
            color: C.bg,
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}>
              TRANSFER SİMÜLASYONU
            </div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.14em',
                color: HEADER.muted,
                marginTop: 3,
              }}
            >
              TASLAK SEÇ VEYA DEĞERLERİ SERBESTÇE DÜZENLE
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: C.gold }}
            >
              {impact.badge}
            </span>
            <button
              onClick={() => dispatch({ type: 'setTransferModal', open: false })}
              style={{
                border: '2px solid ' + C.bg,
                background: 'transparent',
                color: C.bg,
                fontSize: 12,
                fontWeight: 800,
                padding: '4px 10px',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '290px 1fr' }}>
          <div style={{ borderRight: '2px solid ' + C.ink, background: C.surface }}>
            <div
              style={{
                padding: '10px 14px',
                borderBottom: '2px solid ' + C.ink,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em' }}>
                İHTİMALLER
              </span>
              <span style={{ fontSize: 9.5, fontWeight: 600, color: C.muted }}>
                gündemdeki isimler
              </span>
            </div>
            <div
              className="sc"
              style={{ maxHeight: 250, overflow: 'auto', borderBottom: '2px solid ' + C.ink }}
            >
              {lists.targets.map((row) => renderCandidate(row, true))}
            </div>

            <div
              style={{
                padding: '10px 14px',
                borderBottom: '2px solid ' + C.ink,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em' }}>
                TASLAKLAR
              </span>
              <span style={{ fontSize: 9.5, fontWeight: 600, color: C.muted }}>
                isimsiz profiller
              </span>
            </div>
            <div className="sc" style={{ maxHeight: 270, overflow: 'auto' }}>
              {lists.drafts.map((row) => renderCandidate(row, false))}
            </div>

            <div
              style={{
                padding: '11px 14px',
                borderTop: '2px solid ' + C.ink,
                fontSize: 10.5,
                lineHeight: 1.5,
                color: C.ink2,
              }}
            >
              İhtimaller adıyla birlikte yüklenir; taslaklar yalnızca profili (yaş, mevki, değer,
              maaş) doldurur, ismi sen yazarsın. Her değer elle değiştirilebilir. Piyasa değerleri
              temsilîdir.
            </div>
          </div>

          <div>
            <div style={{ padding: '16px 18px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={LABEL_STYLE}>OYUNCU ADI</div>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => dispatch({ type: 'patchForm', patch: { name: e.target.value } })}
                    placeholder="Örn. Can Uzun"
                    style={INPUT_STYLE}
                  />
                </div>
                <div>
                  <div style={LABEL_STYLE}>DOĞUM YILI</div>
                  <input
                    type="number"
                    value={form.birthYear}
                    onChange={(e) =>
                      dispatch({
                        type: 'patchForm',
                        patch: { birthYear: parseFloat(e.target.value) || 0 },
                      })
                    }
                    style={INPUT_STYLE}
                  />
                </div>
                <div>
                  <div style={LABEL_STYLE}>UYRUK KODU</div>
                  <input
                    type="text"
                    value={form.nationality}
                    onChange={(e) =>
                      dispatch({
                        type: 'patchForm',
                        patch: { nationality: String(e.target.value).toUpperCase().slice(0, 3) },
                      })
                    }
                    maxLength={3}
                    style={{ ...INPUT_STYLE, textTransform: 'uppercase' }}
                  />
                </div>
                <div>
                  <div style={LABEL_STYLE}>PİYASA DEĞERİ (M€)</div>
                  <input
                    type="number"
                    value={form.marketValue}
                    onChange={(e) =>
                      dispatch({
                        type: 'patchForm',
                        patch: { marketValue: parseFloat(e.target.value) || 0 },
                      })
                    }
                    step="0.5"
                    style={INPUT_STYLE}
                  />
                </div>
                <div>
                  <div style={LABEL_STYLE}>YILLIK MAAŞ (M€)</div>
                  <input
                    type="number"
                    value={form.wage}
                    onChange={(e) =>
                      dispatch({
                        type: 'patchForm',
                        patch: { wage: parseFloat(e.target.value) || 0 },
                      })
                    }
                    step="0.25"
                    style={INPUT_STYLE}
                  />
                </div>
                <div>
                  <div style={LABEL_STYLE}>REYTİNG</div>
                  <input
                    type="number"
                    value={form.rating}
                    onChange={(e) =>
                      dispatch({
                        type: 'patchForm',
                        patch: { rating: parseFloat(e.target.value) || 0 },
                      })
                    }
                    step="0.05"
                    style={INPUT_STYLE}
                  />
                </div>
                <div>
                  <div style={LABEL_STYLE}>YETİŞME STATÜSÜ</div>
                  <select
                    value={form.homegrown}
                    onChange={(e) =>
                      dispatch({
                        type: 'patchForm',
                        patch: { homegrown: e.target.value as HomegrownStatus },
                      })
                    }
                    style={{
                      width: '100%',
                      border: '2px solid ' + C.ink,
                      background: C.bg,
                      padding: '7px 9px',
                      fontSize: 11.5,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    <option value="yok">Yetiştirilmiş değil</option>
                    <option value="ulke">Türkiye&apos;de yetişti (HG)</option>
                    <option value="kulup">GS altyapısı (KA)</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ ...LABEL_STYLE, marginBottom: 5 }}>
                    MEVKİ AĞIRLIKLARI (TIKLA: 5 → 2 → KAPALI)
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {POSITIONS.map((pos) => {
                      const weight = form.positionWeights[pos.code] ?? 0;
                      // Natural at 4+ reads as solid ink; anything lighter stays outlined.
                      const primary = weight >= WEIGHT_MAX - 1;
                      return (
                        <button
                          key={pos.code}
                          onClick={() =>
                            dispatch({ type: 'cycleFormPosition', position: pos.code })
                          }
                          title={
                            weight
                              ? pos.localizedName + ' · ağırlık ' + weight + '/' + WEIGHT_MAX
                              : pos.localizedName + ' · oynamıyor'
                          }
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            border: '1px solid ' + (weight ? C.ink : C.line),
                            background: primary ? C.ink : weight ? C.surface : C.bg,
                            color: primary ? C.bg : weight ? C.ink : C.muted,
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: '0.06em',
                            padding: '4px 6px 4px 8px',
                            cursor: 'pointer',
                          }}
                        >
                          {pos.localizedCode}
                          <span
                            style={{
                              minWidth: 13,
                              textAlign: 'center',
                              fontSize: 9,
                              padding: '1px 0',
                              background: primary ? C.gold : weight ? C.ink : 'transparent',
                              color: primary ? C.ink : weight ? C.bg : C.line,
                            }}
                          >
                            {weight || '–'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div
                  style={{
                    gridColumn: '1 / -1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    paddingTop: 2,
                  }}
                >
                  <button
                    onClick={() => dispatch({ type: 'toggleTurkeyEligible' })}
                    style={{
                      border: '2px solid ' + C.ink,
                      background: form.turkeyEligible ? C.ink : C.red,
                      color: C.bg,
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      padding: '6px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    A MİLLİ UYGUNLUĞU: {form.turkeyEligible ? 'VAR (YERLİ)' : 'YOK (YABANCI)'}
                  </button>
                  <span style={{ fontSize: 10.5, color: C.muted, fontWeight: 600 }}>
                    Uygun değilse yabancı sayılır.
                  </span>
                </div>
              </div>
            </div>

            <div style={{ borderBottom: '1px solid ' + C.line }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
                {impact.cells.map((cell) => (
                  <div
                    key={cell.label}
                    style={{ padding: '10px 12px', borderRight: '1px solid ' + C.line }}
                  >
                    <div
                      style={{
                        fontSize: 9,
                        letterSpacing: '0.12em',
                        color: C.muted,
                        fontWeight: 700,
                      }}
                    >
                      {cell.label}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>{cell.after}</div>
                    <div style={{ fontSize: 10.5, fontWeight: 800, color: cell.fg }}>
                      {cell.change}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                padding: '12px 16px',
                fontSize: 11.5,
                lineHeight: 1.5,
                color: C.ink2,
                borderBottom: '1px solid ' + C.line,
                textWrap: 'pretty',
              }}
            >
              {impact.warning}
            </div>

            <div style={{ display: 'flex', gap: 0, borderTop: '2px solid ' + C.ink }}>
              <button
                onClick={() => dispatch({ type: 'resetForm' })}
                style={{
                  border: 0,
                  background: C.surface,
                  color: C.ink,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  padding: '14px 16px',
                  cursor: 'pointer',
                }}
              >
                SIFIRLA
              </button>
              <button
                onClick={() => dispatch({ type: 'saveDraft' })}
                style={{
                  border: 0,
                  borderLeft: '2px solid ' + C.ink,
                  background: C.bg,
                  color: C.ink,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  padding: '14px 16px',
                  cursor: 'pointer',
                }}
              >
                TASLAK OLARAK KAYDET
              </button>
              <button
                onClick={() => dispatch({ type: 'addFormPlayer' })}
                style={{
                  flex: 1,
                  border: 0,
                  borderLeft: '2px solid ' + C.ink,
                  background: impact.addBg,
                  color: impact.addFg,
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  padding: '14px 18px',
                  textAlign: 'right',
                  cursor: 'pointer',
                }}
              >
                {impact.addLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
